import { motion } from 'framer-motion';
import { ArrowRight, Cpu, Gauge, Wrench, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';

const Home = () => {
  const stats = [
    { label: '30+ Years', value: 'Industry Experience' },
    { label: '100+', value: 'Projects Completed' },
    { label: '3', value: 'Premium Brands' },
    { label: '100%', value: 'Client Satisfaction' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image with Left-to-Right Fade */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/747tef2x_Building%20Automation.webp')",
            }}
          />
          {/* Much darker overlay fading from left to right */}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-ewss-navy/95 to-ewss-navy/75" />
          {/* Additional gradient for depth */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwRTVGRiIgc3Ryb2tlLXdpZHRoPSIwLjUiIG9wYWNpdHk9IjAuMSIvPjwvZz48L3N2Zz4=')] opacity-20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Powering the Future of <span className="text-ewss-cyan">Industrial Automation</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
              EWSS Automation delivers comprehensive building and industrial automation solutions — from design and implementation to wholesale distribution of premium automation components.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/brands"
                className="px-8 py-4 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all duration-300 flex items-center justify-center group"
                data-testid="explore-brands-btn"
              >
                Explore Brands
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/partner-portal"
                className="px-8 py-4 border-2 border-ewss-cyan text-ewss-cyan font-semibold rounded-lg hover:bg-ewss-cyan/10 transition-all duration-300"
                data-testid="become-partner-btn"
              >
                Become a Partner
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-lg border border-ewss-cyan/20 hover:border-ewss-cyan/50 transition-all">
                <div className="text-3xl md:text-4xl font-bold text-ewss-cyan mb-2">{stat.label}</div>
                <div className="text-sm text-gray-400">{stat.value}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology Partners */}
      <section className="py-20 bg-gradient-to-b from-black to-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-4">
              Our Technology <span className="text-ewss-cyan">Partners</span>
            </h2>
            <p className="text-center text-gray-400 text-lg mb-12 max-w-2xl mx-auto">
              Industry-leading manufacturers delivering premium automation solutions
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <AnimatedSection delay={0.1}>
              <Link to="/brands/haiwell" className="group block">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-ewss-cyan/10 to-transparent border border-ewss-cyan/30 hover:border-ewss-cyan transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,229,255,0.3)] p-8 h-full flex flex-col items-center justify-center">
                  <div className="mb-6 h-32 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/8y0ip6b0_Haiwell%20Logo.png"
                      alt="Haiwell"
                      className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">IoT-Enabled PLCs & Cloud HMIs</p>
                  </div>
                </div>
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <Link to="/brands/supmea" className="group block">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-ewss-red/10 to-transparent border border-ewss-red/30 hover:border-ewss-red transition-all duration-300 hover:shadow-[0_0_30px_rgba(239,68,68,0.3)] p-8 h-full flex flex-col items-center justify-center">
                  <div className="mb-6 h-32 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/axlf7fot_logo-Supmea.png"
                      alt="Supmea"
                      className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Process Instrumentation & Sensors</p>
                  </div>
                </div>
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <Link to="/brands/savch" className="group block">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-ewss-orange/10 to-transparent border border-ewss-orange/30 hover:border-ewss-orange transition-all duration-300 hover:shadow-[0_0_30px_rgba(249,115,22,0.3)] p-8 h-full flex flex-col items-center justify-center">
                  <div className="mb-6 h-32 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/lqroaetq_SAVCH%20Logo.png"
                      alt="Savch"
                      className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">VFD Inverters & Motion Control</p>
                  </div>
                </div>
              </Link>
            </AnimatedSection>

            <AnimatedSection delay={0.4}>
              <Link to="/brands/yotta-control" className="group block" data-testid="home-brand-yotta-link">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-ewss-green/10 to-transparent border border-ewss-green/30 hover:border-ewss-green transition-all duration-300 hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] p-8 h-full flex flex-col items-center justify-center">
                  <div className="mb-6 h-32 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/r7w2zdq2_Yotta%20Controls%20Logo.png"
                      alt="Yotta Control"
                      className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Smart Distributed I/O & Industrial IoT</p>
                  </div>
                </div>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-ewss-cyan text-sm font-semibold uppercase tracking-wider">What We Do</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-4">
              Our Core Services
            </h2>
            <p className="text-center text-gray-400 text-lg mb-12 max-w-3xl mx-auto">
              We are systems integrators and solutions providers — designing, implementing and managing automation across four key disciplines.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 mb-8">
            <AnimatedSection delay={0.1}>
              <div className="relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-ewss-cyan/20 hover:border-ewss-cyan/50 transition-all duration-300 group hover:shadow-[0_0_20px_rgba(0,229,255,0.2)] h-full">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/xss79f18_Building%20Automation.webp"
                    alt="Building Automation"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-ewss-cyan/10 rounded-lg flex items-center justify-center text-ewss-cyan mb-4">
                    <Cpu className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Building Automation Systems</h3>
                  <p className="text-gray-400 mb-4">Integrated control of HVAC, lighting, security and energy across commercial, industrial and residential buildings.</p>
                  <Link to="/solutions" className="inline-flex items-center text-ewss-cyan hover:underline text-sm font-semibold">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-ewss-cyan/20 hover:border-ewss-cyan/50 transition-all duration-300 group hover:shadow-[0_0_20px_rgba(0,229,255,0.2)] h-full">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/22csrq5g_Industrial%20Automation.jpg"
                    alt="Industrial Automation"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-ewss-cyan/10 rounded-lg flex items-center justify-center text-ewss-cyan mb-4">
                    <Gauge className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Industrial Automation</h3>
                  <p className="text-gray-400 mb-4">PLC, SCADA, HMI and process control systems designed and commissioned by our 30-year experienced team.</p>
                  <Link to="/solutions" className="inline-flex items-center text-ewss-cyan hover:underline text-sm font-semibold">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-ewss-cyan/20 hover:border-ewss-cyan/50 transition-all duration-300 group hover:shadow-[0_0_20px_rgba(0,229,255,0.2)] h-full">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/g46q6qqc_Energy%20Eficiency.jpg"
                    alt="Energy Management"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-ewss-cyan/10 rounded-lg flex items-center justify-center text-ewss-cyan mb-4">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Energy Management</h3>
                  <p className="text-gray-400 mb-4">Real-time energy monitoring, sub-metering, demand management and sustainability reporting solutions.</p>
                  <Link to="/solutions" className="inline-flex items-center text-ewss-cyan hover:underline text-sm font-semibold">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.4}>
              <div className="relative overflow-hidden rounded-xl bg-white/5 backdrop-blur-sm border border-ewss-cyan/20 hover:border-ewss-cyan/50 transition-all duration-300 group hover:shadow-[0_0_20px_rgba(0,229,255,0.2)] h-full">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/kmsbb9wy_IOT%20Pic.jpg"
                    alt="IoT Integration"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-ewss-cyan/10 rounded-lg flex items-center justify-center text-ewss-cyan mb-4">
                    <Wrench className="w-6 h-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">IoT Integration</h3>
                  <p className="text-gray-400 mb-4">Connecting plant-floor devices, sensors and systems to cloud platforms via MQTT, Modbus, LoRa and 4G.</p>
                  <Link to="/solutions" className="inline-flex items-center text-ewss-cyan hover:underline text-sm font-semibold">
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={0.5}>
            <div className="text-center mt-8">
              <Link
                to="/solutions"
                className="inline-flex items-center px-8 py-4 bg-ewss-cyan/10 border border-ewss-cyan text-ewss-cyan font-semibold rounded-lg hover:bg-ewss-cyan hover:text-ewss-navy transition-all duration-300 group"
                data-testid="view-all-solutions-btn"
              >
                View All Solutions
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-ewss-navy to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to <span className="text-ewss-cyan">Partner</span> with Us?
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Join our network of manufacturers, integrators, and resellers
            </p>
            <Link
              to="/partner-portal"
              className="inline-flex items-center px-8 py-4 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all duration-300 group"
              data-testid="apply-now-btn"
            >
              Apply Now
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Home;